import { compact, uniqBy } from "lodash";
import { Fragment, useMemo } from "react";
import {
  AssessmentSectionSubjectVisibility,
  AssessmentType,
} from "types/graphql-schema";
import { BasicUser } from "types/topicflow";

import useLabel from "@apps/use-label/use-label";
import { currentUserVar } from "@cache/cache";

import AssessmentSectionSubjectVisibilityBanner from "../assessment/assessment-section-subject-visibility-banner";
import AssessmentQuestionItem from "./assessment-question-item";
import {
  AssessmentAnswer,
  AssessmentAnswerWithResponder,
  AssessmentAnswerWithResponderAndId,
  AssessmentQuestionContainer,
  AssessmentQuestionRole,
  CompetencyCriteriaAssessmentQuestion,
} from "./types";

const RoleBasedAssessmentQuestionGroup = ({
  assessmentType,
  role,
  questions,
  allAnswers,
  otherParticipantsAnswers,
  onUpdateAnswer,
  formDisabled,
  showError,
  isCurrentRole,
  target,
  subjectVisibility,
}: {
  assessmentType: AssessmentType;
  role: AssessmentQuestionRole;
  questions: AssessmentQuestionContainer[];
  allAnswers: AssessmentAnswerWithResponder[];
  otherParticipantsAnswers?: AssessmentAnswerWithResponderAndId[];
  onUpdateAnswer?: (answer: AssessmentAnswer) => void;
  formDisabled?: boolean;
  showError?: boolean;
  isCurrentRole?: boolean;
  target?: BasicUser | null;
  subjectVisibility: AssessmentSectionSubjectVisibility;
}) => {
  const label = useLabel();
  const currentUser = currentUserVar();

  const combinedCriteria = questions.every(
    ({ question }) =>
      question.__typename === "CompetencyAssessmentQuestionNode" ||
      question.__typename === "ResponsibilityAssessmentQuestionNode"
  );
  const separateCriteria = questions.every(
    ({ question }) =>
      question.__typename === "CompetencyCriteriaAssessmentQuestionNode" ||
      question.__typename === "ResponsibilityAssessmentQuestionNode"
  );
  if (!combinedCriteria && !separateCriteria) {
    throw new Error(
      "Role based question group must have either combined or separate criteria"
    );
  }

  const roleQuestionContent = useMemo(() => {
    if (combinedCriteria) {
      const roleQuestions = questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "CompetencyAssessmentQuestionNode"
        );

      return roleQuestions.map((question) => {
        return (
          <AssessmentQuestionItem
            key={question.id}
            assessmentType={assessmentType}
            answer={allAnswers.find(
              (answer) => answer.questionId === question.id
            )}
            otherParticipantsAnswers={otherParticipantsAnswers?.filter(
              (answer) => answer.questionId === question.id
            )}
            question={question}
            onUpdateAnswer={(answer) => {
              if (!onUpdateAnswer) {
                return;
              }
              onUpdateAnswer(answer);
            }}
            formDisabled={!!formDisabled}
            showError={!!showError}
            target={target}
          />
        );
      });
    } else if (separateCriteria) {
      const roleQuestions = questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "CompetencyCriteriaAssessmentQuestionNode"
        );
      const { competencies, roleQuestionsByCompetencyId } =
        roleQuestions.reduce(
          (acc, question) => {
            const competency = question.competencyCriteria.competency;
            const competencyId = competency.id;
            const existing =
              acc.roleQuestionsByCompetencyId[competencyId] ?? [];
            return {
              roleQuestionsByCompetencyId: {
                ...acc.roleQuestionsByCompetencyId,
                [competencyId]: [...existing, question],
              },
              competencies: uniqBy(
                compact([...acc.competencies, competency]),
                "id"
              ),
            };
          },
          { competencies: [], roleQuestionsByCompetencyId: {} } as {
            competencies: {
              id: number;
              title: string;
              description: string;
            }[];
            roleQuestionsByCompetencyId: {
              [competencyId: number]: CompetencyCriteriaAssessmentQuestion[];
            };
          }
        );

      return competencies.map((competency) => {
        const roleQuestions = roleQuestionsByCompetencyId[competency.id];
        if (!roleQuestions || roleQuestions.length === 0) {
          return null;
        }

        return (
          <Fragment key={competency.id}>
            <div className="mt-6 font-medium">{competency.title}</div>
            {roleQuestions.map((question) => {
              return (
                <AssessmentQuestionItem
                  key={question.id}
                  assessmentType={assessmentType}
                  answer={allAnswers.find(
                    (answer) => answer.questionId === question.id
                  )}
                  otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                    (answer) => answer.questionId === question.id
                  )}
                  question={question}
                  onUpdateAnswer={(answer) => {
                    if (!onUpdateAnswer) {
                      return;
                    }
                    onUpdateAnswer(answer);
                  }}
                  formDisabled={!!formDisabled}
                  showError={!!showError}
                  target={target}
                />
              );
            })}
          </Fragment>
        );
      });
    }
    return null;
  }, [
    combinedCriteria,
    separateCriteria,
    questions,
    allAnswers,
    otherParticipantsAnswers,
    formDisabled,
    showError,
    assessmentType,
    target,
    onUpdateAnswer,
  ]);

  const responsiblityQuestions = useMemo(
    () =>
      questions
        .map(({ question }) => question)
        .filter(
          (question) =>
            question.__typename === "ResponsibilityAssessmentQuestionNode"
        ),
    [questions]
  );

  return (
    <>
      <div className="tf-header-lg-dark flex-1 mb-4">
        {`${isCurrentRole ? "Current Role " : "Next Level "} ${label("review", {
          capitalize: true,
        })}: ${role.title} (Level ${role.level})`}
      </div>
      {target && target?.id !== currentUser.id && (
        <AssessmentSectionSubjectVisibilityBanner
          subjectVisibility={subjectVisibility}
          className="-mt-2 mb-4"
        />
      )}
      <div className="flex flex-col gap-4">
        {roleQuestionContent}

        {responsiblityQuestions.length > 0 && (
          <Fragment key="responsibilities">
            <div className="tf-header-base-light mt-6 font-medium">
              Responsibilities
            </div>
            {responsiblityQuestions.map((question) => {
              return (
                <AssessmentQuestionItem
                  key={question.id}
                  assessmentType={assessmentType}
                  answer={allAnswers.find(
                    (answer) => answer.questionId === question.id
                  )}
                  otherParticipantsAnswers={otherParticipantsAnswers?.filter(
                    (answer) => answer.questionId === question.id
                  )}
                  question={question}
                  onUpdateAnswer={(answer) => {
                    if (!onUpdateAnswer) {
                      return;
                    }
                    onUpdateAnswer(answer);
                  }}
                  formDisabled={!!formDisabled}
                  showError={!!showError}
                  target={target}
                />
              );
            })}
          </Fragment>
        )}
      </div>
    </>
  );
};

export default RoleBasedAssessmentQuestionGroup;
